/**
 * Global experiments view: searchable/filterable list of streams across teams (legacy “projects” mental model).
 *
 * Where: `/experiments` (or equivalent route in `App`).
 * Uses: stream/team APIs, `useDebouncedValue`, URL search params for filters.
 * Behavior: desktop table/cards; navigates into `StreamDetails` on row click.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Snackbar,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Button,
  Autocomplete,
  Collapse,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Campaign as CampaignIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { streamApi, StreamDetails } from '@/api/stream';
import { chatApi } from '@/api/chat';
import { teamApi } from '@/api/team';
import { useAuth } from '@/hooks/useAuth';
import { StreamCard, StreamCreateModal, AggregatedStatsCard } from '@/components';

interface StreamsResponse {
  output: {
    items: StreamDetails[];
    next_page_token?: string;
    previous_page_token?: string;
    total_results?: number;
  };
}

export const Experiments: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // URL param helpers: 'all' ↔ '' for "All Status"; missing → 'RUNNING'
  const statusFromUrl = (v: string | null) => {
    if (v === null || v === '') return 'RUNNING';
    if (v === 'all') return '';
    return v;
  };
  const statusToUrl = (v: string) => (v === '' ? 'all' : v);

  // Initialize state from URL params
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string>(() =>
    statusFromUrl(searchParams.get('status'))
  );
  const [sortField, setSortField] = useState<string>(() => searchParams.get('sortField') || 'created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => (searchParams.get('sortDirection') as 'asc' | 'desc') || 'desc');
  // Initialize operator filter as single value
  const [operatorFilter, setOperatorFilter] = useState<string | null>(() => {
    const operatorParam = searchParams.get('operator_uuid') || searchParams.get('operators'); // Support legacy 'operators' param
    return operatorParam || null;
  });
  // Initialize team filter as single value
  const [teamFilter, setTeamFilter] = useState<string | null>(() => {
    const teamParam = searchParams.get('team_uuid');
    return teamParam || null;
  });
  // Initialize date filters
  const [startDateFrom, setStartDateFrom] = useState<string>(() => searchParams.get('start_date_from') || '');
  const [startDateTo, setStartDateTo] = useState<string>(() => searchParams.get('start_date_to') || '');
  const [endDateFrom, setEndDateFrom] = useState<string>(() => searchParams.get('end_date_from') || '');
  const [endDateTo, setEndDateTo] = useState<string>(() => searchParams.get('end_date_to') || '');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  
  // Debounced search term for URL updates (to avoid too many URL changes)
  const searchTermDebounced = useDebouncedValue(searchTerm, 500);

  // Check if user is admin/staff
  const isAdmin = currentUser?.is_staff || currentUser?.is_superuser;

  // Fetch teams to get teamUuid for creating streams
  const { data: teamsData } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamApi.getTeams({ max_results: 1 }),
  });

  // Fetch teams for filter - fetch all teams user has access to (any role: admin, manager, member, owner)
  const {
    data: teamsPages,
    fetchNextPage: fetchNextTeamsPage,
    hasNextPage: hasNextTeamsPage,
    isFetchingNextPage: isFetchingNextTeamsPage,
    isLoading: isLoadingTeams,
  } = useInfiniteQuery({
    queryKey: ['teams-all', currentUser?.key],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      teamApi.getTeams({
        page_token: pageParam,
        max_results: 50,
        any_member_uuid: isAdmin ? undefined : currentUser?.key, // For non-admins, only teams they have access to
      }),
    getNextPageParam: (lastPage) => lastPage.output.next_page_token ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!currentUser,
  });

  // Flatten all teams from all pages
  const allTeams = teamsPages?.pages.flatMap((p) => p.output.items) ?? [];

  // Auto-fetch all pages of teams
  useEffect(() => {
    if (hasNextTeamsPage && !isFetchingNextTeamsPage && !isLoadingTeams) {
      fetchNextTeamsPage();
    }
  }, [hasNextTeamsPage, isFetchingNextTeamsPage, isLoadingTeams, fetchNextTeamsPage]);

  // Fetch operators for filter (admin only) - fetch all without search, filter client-side
  const {
    data: operatorsPages,
    fetchNextPage: fetchNextOperatorsPage,
    hasNextPage: hasNextOperatorsPage,
    isFetchingNextPage: isFetchingNextOperatorsPage,
    isLoading: isLoadingOperators,
  } = useInfiniteQuery({
    queryKey: ['operators-all'],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      chatApi.getProjectOwners({ page_token: pageParam, max_results: 50 }),
    getNextPageParam: (lastPage) => lastPage.next_page_token ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: isAdmin,
  });

  // Flatten all operators from all pages
  const allOperators = operatorsPages?.pages.flatMap((p) => p.items) ?? [];

  // Auto-fetch all pages of operators
  useEffect(() => {
    if (isAdmin && hasNextOperatorsPage && !isFetchingNextOperatorsPage && !isLoadingOperators) {
      fetchNextOperatorsPage();
    }
  }, [isAdmin, hasNextOperatorsPage, isFetchingNextOperatorsPage, isLoadingOperators, fetchNextOperatorsPage]);

  // Helper function to get sort field label
  const getSortLabel = (field: string) => {
    const labels: Record<string, string> = {
      'created_at': 'Created Date',
      'name': 'Name',
      'status': 'Status',
    };
    return labels[field] || field;
  };

  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch streams with infinite scroll
  const {
    data: streamsPages,
    isLoading: isLoadingStreams,
    error: streamsError,
    fetchNextPage: fetchNextStreamsPage,
    hasNextPage: hasNextStreamsPage,
    isFetchingNextPage: isFetchingNextStreamsPage,
  } = useInfiniteQuery({
    queryKey: ['experiments', currentUser?.key, searchTermDebounced, statusFilter, sortField, sortDirection, operatorFilter, teamFilter, startDateFrom, startDateTo, endDateFrom, endDateTo],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      streamApi.getStreams({
        page_token: pageParam,
        max_results: 10,
        search: searchTermDebounced || undefined,
        add_statistics: true,
        status: statusFilter || undefined,
        sort_field: sortField,
        sort_direction: sortDirection,
        operator_uuid: isAdmin && operatorFilter ? operatorFilter : undefined,
        team_uuid: teamFilter || undefined,
        start_date_from: startDateFrom || undefined,
        start_date_to: startDateTo || undefined,
        end_date_from: endDateFrom || undefined,
        end_date_to: endDateTo || undefined,
      }),
    getNextPageParam: (lastPage) => lastPage.output.next_page_token,
    initialPageParam: undefined as string | undefined,
  });

  // Flatten streams from all pages
  const streams: StreamDetails[] = streamsPages?.pages.flatMap((page: StreamsResponse) => page.output.items) ?? [];

  // Get the first team's UUID, or use team from first stream if available
  const teamUuid = teamsData?.output?.items?.[0]?.key || streams?.[0]?.team?.key || '';

  // Fetch aggregated statistics for all filtered streams
  const {
    data: aggregatedStatsData,
    isLoading: isLoadingAggregatedStats,
  } = useQuery({
    queryKey: ['experiments-statistics', currentUser?.key, searchTermDebounced, statusFilter, operatorFilter, teamFilter, startDateFrom, startDateTo, endDateFrom, endDateTo] as [string, string | undefined, string, string, string | null, string | null, string, string, string, string],
    queryFn: () =>
      streamApi.getStreamsStatistics({
        search: searchTermDebounced || undefined,
        status: statusFilter || undefined,
        operator_uuid: isAdmin && operatorFilter ? operatorFilter : undefined,
        team_uuid: teamFilter || undefined,
        start_date_from: startDateFrom || undefined,
        start_date_to: startDateTo || undefined,
        end_date_from: endDateFrom || undefined,
        end_date_to: endDateTo || undefined,
      }),
    enabled: true, // Always fetch when filters change
  });

  // Track if we're updating from internal state to avoid circular updates
  const isInternalUpdateRef = useRef(false);

  // Update URL params when filters change
  const updateURLParams = useCallback((updates: {
    search?: string;
    status?: string;
    sortField?: string;
    sortDirection?: string;
    operator_uuid?: string | null;
    team_uuid?: string | null;
    start_date_from?: string;
    start_date_to?: string;
    end_date_from?: string;
    end_date_to?: string;
  }) => {
    isInternalUpdateRef.current = true;
    const newSearchParams = new URLSearchParams(searchParams);
    
    if (updates.search !== undefined) {
      if (updates.search) {
        newSearchParams.set('search', updates.search);
      } else {
        newSearchParams.delete('search');
      }
    }
    
    if (updates.status !== undefined) {
      const v = statusToUrl(updates.status);
      if (v) {
        newSearchParams.set('status', v);
      } else {
        newSearchParams.delete('status');
      }
    }
    
    if (updates.sortField !== undefined) {
      if (updates.sortField && updates.sortField !== 'created_at') {
        newSearchParams.set('sortField', updates.sortField);
      } else {
        newSearchParams.delete('sortField');
      }
    }
    
    if (updates.sortDirection !== undefined) {
      if (updates.sortDirection && updates.sortDirection !== 'desc') {
        newSearchParams.set('sortDirection', updates.sortDirection);
      } else {
        newSearchParams.delete('sortDirection');
      }
    }
    
    if (updates.operator_uuid !== undefined) {
      if (updates.operator_uuid) {
        newSearchParams.set('operator_uuid', updates.operator_uuid);
      } else {
        newSearchParams.delete('operator_uuid');
        newSearchParams.delete('operators'); // Also remove legacy param
      }
    }
    
    if (updates.team_uuid !== undefined) {
      if (updates.team_uuid) {
        newSearchParams.set('team_uuid', updates.team_uuid);
      } else {
        newSearchParams.delete('team_uuid');
      }
    }
    
    if (updates.start_date_from !== undefined) {
      if (updates.start_date_from) {
        newSearchParams.set('start_date_from', updates.start_date_from);
      } else {
        newSearchParams.delete('start_date_from');
      }
    }
    
    if (updates.start_date_to !== undefined) {
      if (updates.start_date_to) {
        newSearchParams.set('start_date_to', updates.start_date_to);
      } else {
        newSearchParams.delete('start_date_to');
      }
    }
    
    if (updates.end_date_from !== undefined) {
      if (updates.end_date_from) {
        newSearchParams.set('end_date_from', updates.end_date_from);
      } else {
        newSearchParams.delete('end_date_from');
      }
    }
    
    if (updates.end_date_to !== undefined) {
      if (updates.end_date_to) {
        newSearchParams.set('end_date_to', updates.end_date_to);
      } else {
        newSearchParams.delete('end_date_to');
      }
    }
    
    setSearchParams(newSearchParams, { replace: true });
    // Reset flag after a short delay to allow URL to update
    setTimeout(() => {
      isInternalUpdateRef.current = false;
    }, 0);
  }, [searchParams, setSearchParams]);

  // Sync state from URL params when URL changes externally (e.g., browser back button)
  useEffect(() => {
    if (isInternalUpdateRef.current) {
      return; // Skip if this is from our own internal update
    }

    const urlSearch = searchParams.get('search') || '';
    const urlStatusParam = searchParams.get('status');
    const urlStatus = urlStatusParam !== null ? statusFromUrl(urlStatusParam) : statusFilter;
    const urlSortField = searchParams.get('sortField') || 'created_at';
    const urlSortDirection = (searchParams.get('sortDirection') as 'asc' | 'desc') || 'desc';
    const urlOperator = searchParams.get('operator_uuid') || searchParams.get('operators') || null; // Support legacy 'operators' param
    const urlTeam = searchParams.get('team_uuid') || null;
    const urlStartDateFrom = searchParams.get('start_date_from') || '';
    const urlStartDateTo = searchParams.get('start_date_to') || '';
    const urlEndDateFrom = searchParams.get('end_date_from') || '';
    const urlEndDateTo = searchParams.get('end_date_to') || '';

    // Only update state if URL values differ from current state
    if (urlSearch !== searchTerm) {
      setSearchTerm(urlSearch);
    }
    // Only update status if URL explicitly has it or if it's different from current
    if (urlStatusParam !== null && urlStatus !== statusFilter) {
      setStatusFilter(urlStatus);
    }
    if (urlSortField !== sortField) {
      setSortField(urlSortField);
    }
    if (urlSortDirection !== sortDirection) {
      setSortDirection(urlSortDirection);
    }
    if (urlOperator !== operatorFilter) {
      setOperatorFilter(urlOperator);
    }
    if (urlTeam !== teamFilter) {
      setTeamFilter(urlTeam);
    }
    if (urlStartDateFrom !== startDateFrom) {
      setStartDateFrom(urlStartDateFrom);
    }
    if (urlStartDateTo !== startDateTo) {
      setStartDateTo(urlStartDateTo);
    }
    if (urlEndDateFrom !== endDateFrom) {
      setEndDateFrom(urlEndDateFrom);
    }
    if (urlEndDateTo !== endDateTo) {
      setEndDateTo(urlEndDateTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Single effect: sync all filters to URL so no overwrites (e.g. status lost on reload)
  useEffect(() => {
    updateURLParams({
      search: searchTermDebounced,
      status: statusFilter,
      sortField,
      sortDirection,
      operator_uuid: operatorFilter,
      team_uuid: teamFilter,
      start_date_from: startDateFrom,
      start_date_to: startDateTo,
      end_date_from: endDateFrom,
      end_date_to: endDateTo,
    });
  }, [
    updateURLParams,
    searchTermDebounced,
    statusFilter,
    sortField,
    sortDirection,
    operatorFilter,
    teamFilter,
    startDateFrom,
    startDateTo,
    endDateFrom,
    endDateTo,
  ]);

  // Intersection observer for infinite scroll
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextStreamsPage && !isFetchingNextStreamsPage) {
          fetchNextStreamsPage();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextStreamsPage, isFetchingNextStreamsPage, fetchNextStreamsPage]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant={isMobile ? "h5" : "h4"} component="h1" sx={{ fontWeight: 700, mb: 1 }}>
              Experiments
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View and manage all your campaigns and experiments.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              if (!teamUuid) {
                setToast({
                  open: true,
                  message: 'No team available. Please create a team first.',
                  severity: 'error',
                });
                return;
              }
              setCreateModalOpen(true);
            }}
            disabled={!teamUuid}
            sx={{ flexShrink: 0 }}
          >
            Create Experiment
          </Button>
        </Box>

        {/* Query Bar */}
        <Box sx={{ 
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: showAdvancedFilters ? '8px 8px 0 0' : 2,
          borderBottom: showAdvancedFilters ? 'none' : undefined,
        }}>
          {/* Main Filter Row */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 2, sm: 2 }, 
            p: 2, 
          }}>
            {/* Search Input - Full width on mobile */}
          <TextField
            placeholder="Search experiments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 200 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />

          {/* Active Filters Chips - Stack on mobile */}
          <Stack 
            direction="row" 
            spacing={0.1}
            sx={{ 
              flex: { xs: 'none', sm: 1 }, 
              minWidth: 0,
              flexWrap: 'wrap',
              justifyContent: { xs: 'flex-start', sm: 'flex-start' },
              gap: 0.1,
              '& .MuiChip-root': {
                mb: { xs: 0.1, sm: 0.1 },
                mt: { xs: 0.1, sm: 0.1 }
              }
            }}
          >
            {statusFilter && statusFilter !== '' && (
              <Chip
                label={`Status: ${statusFilter}`}
                size="small"
                onDelete={() => setStatusFilter('')}
                color="primary"
                variant="outlined"
              />
            )}
            {searchTerm && (
              <Chip
                label={`Search: "${searchTerm}"`}
                size="small"
                onDelete={() => setSearchTerm('')}
                color="secondary"
                variant="outlined"
              />
            )}
            {isAdmin && operatorFilter && (
              <Chip
                label={`Operator: ${allOperators.find(op => op.uuid === operatorFilter)?.name || operatorFilter}`}
                size="small"
                onDelete={() => setOperatorFilter(null)}
                color="info"
                variant="outlined"
              />
            )}
            {teamFilter && (
              <Chip
                label={`Team: ${allTeams.find(team => team.key === teamFilter)?.name || teamFilter}`}
                size="small"
                onDelete={() => setTeamFilter(null)}
                color="secondary"
                variant="outlined"
              />
            )}
            {startDateFrom && (
              <Chip
                label={`Start From: ${startDateFrom}`}
                size="small"
                onDelete={() => setStartDateFrom('')}
                color="info"
                variant="outlined"
              />
            )}
            {startDateTo && (
              <Chip
                label={`Start To: ${startDateTo}`}
                size="small"
                onDelete={() => setStartDateTo('')}
                color="info"
                variant="outlined"
              />
            )}
            {endDateFrom && (
              <Chip
                label={`End From: ${endDateFrom}`}
                size="small"
                onDelete={() => setEndDateFrom('')}
                color="info"
                variant="outlined"
              />
            )}
            {endDateTo && (
              <Chip
                label={`End To: ${endDateTo}`}
                size="small"
                onDelete={() => setEndDateTo('')}
                color="info"
                variant="outlined"
              />
            )}
            <Chip
              label={`Sort: ${getSortLabel(sortField)} ${sortDirection === 'desc' ? '↓' : '↑'}`}
              size="small"
              onDelete={() => {
                setSortField('created_at');
                setSortDirection('desc');
              }}
              color="default"
              variant="outlined"
            />
          </Stack>

          {/* Filter and Sort Controls - Stack on mobile */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            {/* Status Filter */}
            <FormControl 
              size="small" 
              sx={{ 
                minWidth: { xs: '100%', sm: 120 },
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                displayEmpty
                startAdornment={<FilterIcon fontSize="small" sx={{ mr: 1 }} />}
                sx={{
                  '& .MuiSelect-select': { 
                    padding: '8px 12px', 
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  borderRadius: 2,
                }}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="RUNNING">Running</MenuItem>
                <MenuItem value="PAUSED">Paused</MenuItem>
                <MenuItem value="DONE">Done</MenuItem>
                <MenuItem value="WAITING">Waiting</MenuItem>
                <MenuItem value="WITHDRAW">Withdraw</MenuItem>
                <MenuItem value="ARCHIVE">Archive</MenuItem>
              </Select>
            </FormControl>

            {/* Operator Filter (Admin only) - Single select with autocomplete */}
            {isAdmin && (
              <Autocomplete
                size="small"
                options={allOperators}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                value={allOperators.find((op) => op.uuid === operatorFilter) || null}
                onChange={(_, newValue) => {
                  setOperatorFilter(newValue?.uuid || null);
                }}
                loading={isLoadingOperators || isFetchingNextOperatorsPage}
                filterOptions={(options, state) => {
                  // Filter based on current input value
                  if (!state.inputValue) {
                    return options;
                  }
                  const searchLower = state.inputValue.toLowerCase();
                  return options.filter((option) =>
                    option.name.toLowerCase().includes(searchLower)
                  );
                }}
                ListboxProps={{
                  onScroll: (event: React.SyntheticEvent) => {
                    const listboxNode = event.currentTarget;
                    if (
                      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
                      hasNextOperatorsPage &&
                      !isFetchingNextOperatorsPage
                    ) {
                      fetchNextOperatorsPage();
                    }
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search operators..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <FilterIcon fontSize="small" sx={{ mr: 1 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      minWidth: { xs: '100%', sm: 200 },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                )}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { xs: '100%', sm: 200 },
                }}
              />
            )}

            {/* Team Filter - Single select with autocomplete */}
            <Autocomplete
              size="small"
              options={allTeams}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              value={allTeams.find((team) => team.key === teamFilter) || null}
              onChange={(_, newValue) => {
                setTeamFilter(newValue?.key || null);
              }}
              loading={isLoadingTeams || isFetchingNextTeamsPage}
              filterOptions={(options, state) => {
                // Filter based on current input value
                if (!state.inputValue) {
                  return options;
                }
                const searchLower = state.inputValue.toLowerCase();
                return options.filter((option) =>
                  option.name.toLowerCase().includes(searchLower)
                );
              }}
              ListboxProps={{
                onScroll: (event: React.SyntheticEvent) => {
                  const listboxNode = event.currentTarget;
                  if (
                    listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
                    hasNextTeamsPage &&
                    !isFetchingNextTeamsPage
                  ) {
                    fetchNextTeamsPage();
                  }
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search teams..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <FilterIcon fontSize="small" sx={{ mr: 1 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    minWidth: { xs: '100%', sm: 200 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              )}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: '100%', sm: 200 },
              }}
            />

            {/* Sort Field with Direction Toggle */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              alignItems: 'center',
              width: { xs: '100%', sm: 'auto' }
            }}>
              <FormControl 
                size="small" 
                sx={{ 
                  minWidth: { xs: 'calc(100% - 50px)', sm: 120 },
                  width: { xs: 'calc(100% - 50px)', sm: 'auto' }
                }}
              >
                <Select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value)}
                  startAdornment={<SortIcon fontSize="small" sx={{ mr: 1 }} />}
                  sx={{
                    '& .MuiSelect-select': { 
                      padding: '8px 12px', 
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center'
                    },
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="created_at">Created Date</MenuItem>
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                </Select>
              </FormControl>

              {/* Sort Direction Toggle - Right next to sort field */}
              <Tooltip title={`Sort ${sortDirection === 'desc' ? 'Ascending' : 'Descending'}`}>
                <IconButton
                  size="small"
                  onClick={() => setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc')}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 1,
                    minWidth: 40,
                    minHeight: 40,
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'primary.50'
                    }
                  }}
                >
                  {sortDirection === 'desc' ? <ArrowDownIcon fontSize="small" /> : <ArrowUpIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          </Box>

          {/* Advanced Filters Toggle - Bottom center (only when closed) */}
          {!showAdvancedFilters && (
            <Box sx={{ 
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              pb: 1
            }}>
              <Tooltip title="Show Advanced Filters">
                <IconButton
                  size="small"
                  onClick={() => setShowAdvancedFilters(true)}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 0.5,
                    minWidth: 24,
                    minHeight: 24,
                    width: 24,
                    height: 24,
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'primary.50'
                    }
                  }}
                >
                  <ExpandMoreIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Advanced Filters Section - Connected to main section */}
        <Collapse in={showAdvancedFilters}>
          <Box sx={{ 
            px: 2,
            pt: 0,
            pb: 0,
            bgcolor: 'background.paper',
            borderLeft: 1,
            borderRight: 1,
            borderBottom: 1,
            borderColor: 'divider',
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px',
            mt: 0,
          }}>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 3
            }}>
              {/* Start Date Range */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 500 }}>
                  Start Date
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    label="From"
                    type="date"
                    value={startDateFrom}
                    onChange={(e) => setStartDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="To"
                    type="date"
                    value={startDateTo}
                    onChange={(e) => setStartDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Box>
              </Box>
              
              {/* End Date Range */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 500 }}>
                  End Date
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    label="From"
                    type="date"
                    value={endDateFrom}
                    onChange={(e) => setEndDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="To"
                    type="date"
                    value={endDateTo}
                    onChange={(e) => setEndDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Box>
              </Box>
            </Box>
            
            {/* Toggle button at bottom of section - centered */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0, pt: '3px', pb: '3px', borderTop: 1, borderColor: 'divider' }}>
              <Tooltip title="Hide Advanced Filters">
                <IconButton
                  size="small"
                  onClick={() => setShowAdvancedFilters(false)}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 0.5,
                    minWidth: 24,
                    minHeight: 24,
                    width: 24,
                    height: 24,
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'primary.50'
                    }
                  }}
                >
                  <ExpandLessIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {/* Aggregated Statistics Card */}
        {aggregatedStatsData?.output && (
          <AggregatedStatsCard
            statistics={aggregatedStatsData.output}
            isLoading={isLoadingAggregatedStats}
          />
        )}

        {streamsError ? (
          <Alert severity="error">
            Failed to load experiments. Please try again.
          </Alert>
        ) : isLoadingStreams && streams.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : streams.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CampaignIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No experiments found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm || statusFilter || operatorFilter || teamFilter
                ? 'Try adjusting your filters to see more results.'
                : 'Experiments will appear here once created.'}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Stream Cards */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {streams.map((stream: StreamDetails) => (
                <StreamCard
                  key={stream.key}
                  stream={stream}
                />
              ))}
            </Box>

            {/* Loading indicator for infinite scroll */}
            {isFetchingNextStreamsPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {/* Load more trigger */}
            <div ref={loadMoreRef} style={{ height: 20 }} />
          </>
        )}
      </Box>

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Create Experiment Modal */}
        <StreamCreateModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
        />
    </Box>
  );
};

